import { VariablePalette } from '@/components/admin/protocoles/step/variable-palette';
import { PaintedTextField } from '@/components/admin/variables/painted-text-field';
import {
  firstPaletteItem,
  paletteGroups,
  protocolVariableDescriber,
} from '@/lib/protocol-step-draft';
import type { VariableZone } from '@/lib/variable-pill';
import type { CriterionCard, ProtocolStepDraft } from '@matter_extractor/shared';
import { variableToken } from '@matter_extractor/shared';
import { useMemo } from 'react';

/**
 * A direction of the protocol editor: the painted field of §8.6, dressed for
 * a step, an objective or an action (§7.2 ; grill Variables du 2026-09-16, D5).
 *
 * §8.6 stores `{{objectif:id}}` and displays « @Vécu marquant », « pour qu'un
 * renommage n'invalide rien ». This field was the first to draw that pill, and
 * it carried the whole mechanism - the imperative paint, the read back, the
 * `@` on the text before the caret, the atom the caret cannot split. Issue #88
 * generalised it into `variables/painted-text-field.tsx` so the Contexte, the
 * overlay and a Checkbox label draw the very same atom, and what is left here
 * is what a PROTOCOL knows that an experience does not:
 *
 *   - the names of its tokens come from the steps being edited and from the
 *     criteria library (`protocolVariableDescriber`), never from an experience;
 *   - the palette is the protocol's own three groups (`paletteGroups`: the
 *     objectives of this step, those of the steps before it, the criteria) and
 *     not the position-aware four of an experience (D3): a direction is served
 *     during the protocol, so everything the protocol knows is in reach;
 *   - the fiche has no « Ouvrir » to offer - the objective it describes is on
 *     this very screen - and no page to say « Rend ici » for: an objective in a
 *     direction always renders the interviewer's matter.
 *
 * The props are the ones the three callers (`step-panel`, `objective-row`,
 * `exit-conditions`) have always passed: nothing about them changed.
 */

export interface DirectionFieldProps {
  /** The direction as it is STORED: literal text plus `{{objectif:id}}` tokens. */
  value: string;
  onChange: (next: string) => void;
  /** « Voir » or a retired protocol: readable, selectable, not typable. */
  readOnly: boolean;
  placeholder: string;
  ariaLabel: string;
  testId: string;
  /**
   * The mark of the `{ }` button, or null.
   *
   * Only the step's own direction carries `insertVariable`: the mark is read
   * across seams (the e2e spec, the capture script), and four fields carrying
   * it on one screen would be a selector that resolves to four elements. The
   * button itself is on every field, because §8.6 puts it on every text zone.
   */
  insertTestId: string | null;
  steps: readonly ProtocolStepDraft[];
  stepId: string;
  criteria: readonly CriterionCard[];
  /** The height of the box: a step's direction is taller than an action's. */
  minHeightClass: string;
}

export function DirectionField({
  value,
  onChange,
  readOnly,
  placeholder,
  ariaLabel,
  testId,
  insertTestId,
  steps,
  stepId,
  criteria,
  minHeightClass,
}: DirectionFieldProps) {
  /*
    Memoised on what it reads, because the painted field repaints when the
    describer's answers change and compares signatures otherwise: a describer
    rebuilt on every render with the same steps would cost a segmentation per
    keystroke for nothing.
  */
  const zone = useMemo<VariableZone>(
    () => ({
      describe: protocolVariableDescriber(steps, criteria),
      place: null,
      experienceId: null,
    }),
    [steps, criteria],
  );

  return (
    <PaintedTextField
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      placeholder={placeholder}
      ariaLabel={ariaLabel}
      testId={testId}
      insertTestId={insertTestId}
      minHeightClass={minHeightClass}
      zone={zone}
      renderMenu={(query, insert) => (
        <VariablePalette
          groups={paletteGroups(steps, stepId, criteria, query ?? '')}
          query={query}
          onPick={(variable) => insert(variableToken(variable))}
        />
      )}
      firstToken={(query) => {
        const first = firstPaletteItem(paletteGroups(steps, stepId, criteria, query));
        return first === null ? null : variableToken(first.variable);
      }}
    />
  );
}
